/**
 * Scan timing estimates — rolling rate + prior-run baselines per folder.
 * Phase 1 (NAS walk) has no total upfront; we learn from the last completed scan.
 */

export interface ScanTimingBaseline {
  /** Filesystem entries counted during NAS walk (not audio file count). */
  walkEntries: number;
  /** Audio files tagged in phase 2. */
  tagFiles: number;
  /** Milliseconds spent in phase 1. */
  walkMs: number;
  /** Milliseconds spent in phase 2. */
  tagMs: number;
  updatedAt: number;
}

const STORAGE_KEY = 'maestro_scan_timing_baselines';
const MAX_BASELINES = 24;

function normPathKey(path: string): string {
  return path.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();
}

function readAllBaselines(): Record<string, ScanTimingBaseline> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAllBaselines(map: Record<string, ScanTimingBaseline>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota or private mode */
  }
}

export function getScanBaseline(path: string | null | undefined): ScanTimingBaseline | null {
  if (!path) return null;
  const key = normPathKey(path);
  return readAllBaselines()[key] ?? null;
}

export function saveScanBaseline(path: string, data: Omit<ScanTimingBaseline, 'updatedAt'>): void {
  if (!path) return;
  const key = normPathKey(path);
  const all = readAllBaselines();
  all[key] = { ...data, updatedAt: Date.now() };
  const keys = Object.keys(all).sort(
    (a, b) => (all[b]?.updatedAt ?? 0) - (all[a]?.updatedAt ?? 0),
  );
  if (keys.length > MAX_BASELINES) {
    keys.slice(MAX_BASELINES).forEach((k) => delete all[k]);
  }
  writeAllBaselines(all);
}

/** @param totalSec seconds */
export function formatDuration(totalSec: number | null | undefined): string {
  if (totalSec == null || !Number.isFinite(totalSec)) return '';
  const s = Math.max(0, Math.round(totalSec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  }
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

export type ScanTimerParts = {
  stepLabel: string | null;
  elapsedLabel: string;
  detailLabel: string | null;
  remainingSec: number | null;
};

/**
 * Build human-readable timer line for the progress strip.
 */
export function buildScanTimerParts(opts: {
  phase: string;
  elapsedSec: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  etaMs?: number | null;
  filesPerSecond?: number | null;
  baselineKey?: string | null;
  /** Client-computed items/sec during NAS walk when engine does not send rate. */
  walkRate?: number | null;
}): ScanTimerParts {
  const {
    phase,
    elapsedSec,
    currentStep,
    totalSteps,
    etaMs,
    filesPerSecond,
    baselineKey,
    walkRate,
  } = opts;

  const elapsedLabel = formatDuration(elapsedSec) || '0s';

  if (phase === 'enumerating' || phase === 'discovering') {
    const rate = effectiveWalkRate({
      filesPerSecond,
      walkRate,
      currentStep,
      phaseElapsedSec: elapsedSec,
    });
    const count = typeof currentStep === 'number' ? currentStep : 0;

    const remainingSec = estimateWalkRemainingSec({
      currentStep,
      filesPerSecond,
      walkRate,
      baselineKey,
      phaseElapsedSec: elapsedSec,
    });

    let detailLabel: string | null = null;
    if (rate != null && rate >= 1) {
      detailLabel = `~${Math.round(rate).toLocaleString()} items/sec on NAS`;
    }
    if (remainingSec != null) {
      const rem = formatDuration(remainingSec);
      detailLabel = detailLabel
        ? `${detailLabel} · ~${rem} left`
        : `~${rem} left`;
    } else if (!detailLabel) {
      detailLabel = 'calculating…';
    }

    return {
      stepLabel: 'Step 1 of 2 — NAS walk',
      elapsedLabel,
      detailLabel,
      remainingSec,
    };
  }

  if (phase === 'scanning') {
    const etaSec = typeof etaMs === 'number' && etaMs >= 0
      ? Math.round(etaMs / 1000)
      : null;
    const rate = filesPerSecond && filesPerSecond > 0 ? filesPerSecond : null;
    let detailLabel: string | null = null;

    if (etaSec != null) {
      detailLabel = `~${formatDuration(etaSec)} remaining`;
    } else if (
      typeof currentStep === 'number'
      && typeof totalSteps === 'number'
      && totalSteps > 0
      && elapsedSec > 5
    ) {
      const frac = currentStep / totalSteps;
      if (frac > 0.02 && frac < 1) {
        const est = Math.max(0, Math.round((elapsedSec / frac) - elapsedSec));
        detailLabel = `~${formatDuration(est)} remaining`;
      }
    }

    if (rate != null) {
      detailLabel = detailLabel
        ? `${detailLabel} · ~${Math.round(rate)} files/sec`
        : `~${Math.round(rate)} files/sec`;
    }

    return {
      stepLabel: 'Step 2 of 2 — reading tags',
      elapsedLabel,
      detailLabel: detailLabel ?? 'estimating…',
      remainingSec: etaSec,
    };
  }

  if (phase === 'finalizing' || phase === 'indexing') {
    return {
      stepLabel: 'Step 3 — updating library',
      elapsedLabel,
      detailLabel: '~30s remaining',
      remainingSec: 30,
    };
  }

  return {
    stepLabel: null,
    elapsedLabel,
    detailLabel: null,
    remainingSec: null,
  };
}

export function formatScanTimerLine(parts: ScanTimerParts): string {
  const chunks = [
    parts.stepLabel,
    `${parts.elapsedLabel} elapsed`,
    parts.detailLabel,
  ].filter(Boolean);
  return chunks.join(' · ');
}

/** Clock display for elapsed / countdown (m:ss). */
export function formatClock(totalSec: number | null | undefined): string {
  if (totalSec == null || !Number.isFinite(totalSec)) return '—';
  const s = Math.max(0, Math.round(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export type PipelineStepStatus = 'pending' | 'active' | 'done';

export type ScanPipelineStep = {
  id: 'walk' | 'tags' | 'finalize';
  title: string;
  status: PipelineStepStatus;
  /** Static estimate shown while pending (e.g. "~45m est."). */
  pendingEstimate: string | null;
  /** Estimated seconds for pending step (for total ETA math). */
  pendingEstimateSec: number | null;
};

export type ScanPipeline = {
  steps: ScanPipelineStep[];
  activeStepId: ScanPipelineStep['id'] | null;
  /** Detail line for the active step (rate, file counts, etc.). */
  activeDetail: string | null;
  /** Raw remaining seconds before live countdown smoothing. */
  activeRemainingSec: number | null;
};

const FINALIZE_EST_SEC = 30;

function getAllBaselines(): ScanTimingBaseline[] {
  return Object.values(readAllBaselines());
}

function globalMsPerWalkEntry(): number | null {
  const all = getAllBaselines();
  if (!all.length) return null;
  const sum = all.reduce((acc, b) => acc + b.walkMs / Math.max(b.walkEntries, 1), 0);
  return sum / all.length;
}

function globalTagWalkRatio(): number {
  const all = getAllBaselines();
  if (!all.length) return 1.8;
  const ratios = all
    .filter((b) => b.walkMs > 0)
    .map((b) => b.tagMs / b.walkMs);
  if (!ratios.length) return 1.8;
  return ratios.reduce((a, b) => a + b, 0) / ratios.length;
}

function findSimilarWalkBaseline(entries: number): ScanTimingBaseline | null {
  const all = getAllBaselines();
  if (!all.length) return null;
  const sorted = [...all].sort((a, b) => {
    const da = Math.abs(a.walkEntries - entries);
    const db = Math.abs(b.walkEntries - entries);
    return da - db;
  });
  return sorted[0] ?? null;
}

function effectiveWalkRate(opts: {
  filesPerSecond?: number | null;
  walkRate?: number | null;
  currentStep?: number | null;
  phaseElapsedSec?: number;
}): number | null {
  if (opts.filesPerSecond && opts.filesPerSecond > 0) return opts.filesPerSecond;
  if (opts.walkRate && opts.walkRate > 0) return opts.walkRate;
  const count = typeof opts.currentStep === 'number' ? opts.currentStep : 0;
  const elapsed = opts.phaseElapsedSec ?? 0;
  if (count > 200 && elapsed >= 15) return count / elapsed;
  return null;
}

function applyWalkAntiStallFloor(
  remaining: number,
  count: number,
  rate: number,
  elapsed: number,
): number {
  if (rate < 15 || elapsed < 30) return Math.max(0, remaining);
  const minFromRate = Math.round((count * 0.06) / rate);
  const minFromElapsed = Math.round(elapsed * 0.2);
  const floor = Math.min(Math.max(minFromRate, minFromElapsed), 900);
  return Math.max(remaining, floor);
}

function estimateWalkRemainingSec(opts: {
  currentStep?: number | null;
  filesPerSecond?: number | null;
  walkRate?: number | null;
  baselineKey?: string | null;
  phaseElapsedSec?: number;
}): number | null {
  const count = typeof opts.currentStep === 'number' ? opts.currentStep : 0;
  const elapsed = opts.phaseElapsedSec ?? 0;
  const rate = effectiveWalkRate(opts);
  if (rate == null || rate < 1 || count < 100) return null;

  const pathBaseline = getScanBaseline(opts.baselineKey ?? null);
  const similarBaseline = findSimilarWalkBaseline(count);
  const baseline = pathBaseline ?? similarBaseline;

  let remaining: number | null = null;

  if (baseline && count > 200) {
    const baselineTarget = baseline.walkEntries;

    if (count <= baselineTarget * 1.02) {
      remaining = Math.round((baselineTarget - count) / rate);
      if (remaining <= 0) {
        remaining = Math.round((count * 0.05) / rate);
      }
    }
  }

  if (remaining == null || (pathBaseline && count > pathBaseline.walkEntries * 1.02)) {
    const extraFraction = Math.max(0.05, Math.min(0.2, 0.18 - elapsed / 7200));
    remaining = Math.round((count * extraFraction) / rate);
  }

  if (remaining == null && baseline && count > 500) {
    const msPerEntry = globalMsPerWalkEntry();
    if (msPerEntry != null) {
      const projectedTotal = Math.max(count * 1.12, count + rate * 45);
      const estTotalSec = (projectedTotal * msPerEntry) / 1000;
      remaining = Math.round(estTotalSec - elapsed);
    }
  }

  if (remaining == null && elapsed >= 20 && count > 500) {
    const progressFraction = Math.min(0.75, elapsed / (elapsed + 480));
    const estimatedTotalEntries = count / Math.max(progressFraction, 0.12);
    remaining = Math.round((estimatedTotalEntries - count) / rate);
  }

  if (remaining == null) return null;

  if (remaining <= 0 && rate > 10) {
    remaining = Math.round((count * 0.04) / rate);
  }

  remaining = applyWalkAntiStallFloor(Math.max(0, remaining), count, rate, elapsed);
  return remaining;
}

function estimateTagPendingSec(opts: {
  baselineKey?: string | null;
  walkRemainingSec: number | null;
  currentStep?: number | null;
  phaseElapsedSec?: number;
}): { sec: number | null; label: string } {
  const baseline = getScanBaseline(opts.baselineKey ?? null);
  if (baseline?.tagMs) {
    const sec = Math.max(30, Math.round(baseline.tagMs / 1000));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  const count = typeof opts.currentStep === 'number' ? opts.currentStep : 0;
  const similar = count > 0 ? findSimilarWalkBaseline(count) : null;
  if (similar?.tagMs) {
    const sec = Math.max(30, Math.round(similar.tagMs / 1000));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  if (opts.walkRemainingSec != null) {
    const sec = Math.max(60, Math.round(opts.walkRemainingSec * globalTagWalkRatio()));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  const elapsed = opts.phaseElapsedSec ?? 0;
  if (elapsed >= 30) {
    const sec = Math.max(60, Math.round(elapsed * globalTagWalkRatio()));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  return { sec: null, label: 'calculating…' };
}

function estimateTagRemainingSec(opts: {
  elapsedSec: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  etaMs?: number | null;
}): number | null {
  if (typeof opts.etaMs === 'number' && opts.etaMs >= 0) {
    return Math.round(opts.etaMs / 1000);
  }
  if (
    typeof opts.currentStep === 'number'
    && typeof opts.totalSteps === 'number'
    && opts.totalSteps > 0
    && opts.elapsedSec > 5
  ) {
    const frac = opts.currentStep / opts.totalSteps;
    if (frac > 0.02 && frac < 1) {
      return Math.max(0, Math.round((opts.elapsedSec / frac) - opts.elapsedSec));
    }
  }
  return null;
}

/**
 * Three-step scan pipeline: NAS walk → tag read → library refresh.
 */
export function buildScanPipeline(opts: {
  phase: string;
  elapsedSec: number;
  phaseElapsedSec?: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  etaMs?: number | null;
  filesPerSecond?: number | null;
  baselineKey?: string | null;
  walkRate?: number | null;
}): ScanPipeline {
  const phaseElapsedSec = opts.phaseElapsedSec ?? 0;
  const baseline = getScanBaseline(opts.baselineKey ?? null);

  const phase = opts.phase;
  const isWalk = phase === 'enumerating' || phase === 'discovering' || phase === 'preparing';
  const isTags = phase === 'scanning';
  const isFinalize = phase === 'finalizing' || phase === 'indexing';

  const walkRemaining = isWalk
    ? estimateWalkRemainingSec({ ...opts, phaseElapsedSec })
    : null;

  const tagPending = isWalk
    ? estimateTagPendingSec({
      baselineKey: opts.baselineKey,
      walkRemainingSec: walkRemaining,
      currentStep: opts.currentStep,
      phaseElapsedSec,
    })
    : { sec: null, label: null };

  const tagEstSec = isTags
    ? null
    : (baseline?.tagMs ? Math.max(30, Math.round(baseline.tagMs / 1000)) : tagPending.sec);

  const tagPendingLabel = isWalk
    ? (tagPending.label ?? 'calculating…')
    : null;

  const tagRemaining = isTags
    ? estimateTagRemainingSec({
      elapsedSec: phaseElapsedSec,
      currentStep: opts.currentStep,
      totalSteps: opts.totalSteps,
      etaMs: opts.etaMs,
    })
    : null;

  let activeDetail: string | null = null;
  if (isWalk) {
    const rate = (opts.filesPerSecond && opts.filesPerSecond > 0)
      ? opts.filesPerSecond
      : (opts.walkRate && opts.walkRate > 0 ? opts.walkRate : null);
    const count = typeof opts.currentStep === 'number' ? opts.currentStep : 0;
    if (count > 0) activeDetail = `${count.toLocaleString()} items found`;
    if (rate != null && rate >= 1) {
      activeDetail = activeDetail
        ? `${activeDetail} · ~${Math.round(rate).toLocaleString()}/sec`
        : `~${Math.round(rate).toLocaleString()} items/sec on NAS`;
    }
  } else if (isTags) {
    const cur = typeof opts.currentStep === 'number' ? opts.currentStep : null;
    const tot = typeof opts.totalSteps === 'number' ? opts.totalSteps : null;
    if (cur != null && tot != null) activeDetail = `${cur.toLocaleString()} of ${tot.toLocaleString()} files`;
    const rate = opts.filesPerSecond && opts.filesPerSecond > 0 ? opts.filesPerSecond : null;
    if (rate != null) {
      activeDetail = activeDetail
        ? `${activeDetail} · ~${Math.round(rate)}/sec`
        : `~${Math.round(rate)} files/sec`;
    }
  } else if (isFinalize) {
    activeDetail = 'Refreshing album grid';
  }

  const steps: ScanPipelineStep[] = [
    {
      id: 'walk',
      title: 'Walking NAS folders',
      status: isWalk ? 'active' : (isTags || isFinalize ? 'done' : 'pending'),
      pendingEstimate: null,
      pendingEstimateSec: null,
    },
    {
      id: 'tags',
      title: 'Reading file tags',
      status: isTags ? 'active' : isFinalize ? 'done' : 'pending',
      pendingEstimate: isWalk ? tagPendingLabel : null,
      pendingEstimateSec: isWalk ? tagEstSec : null,
    },
    {
      id: 'finalize',
      title: 'Updating album library',
      status: isFinalize ? 'active' : 'pending',
      pendingEstimate: (isWalk || isTags) ? `~${formatDuration(FINALIZE_EST_SEC)} est.` : null,
      pendingEstimateSec: (isWalk || isTags) ? FINALIZE_EST_SEC : null,
    },
  ];

  const activeStepId = isWalk ? 'walk' : isTags ? 'tags' : isFinalize ? 'finalize' : null;
  const activeRemainingSec = isWalk
    ? walkRemaining
    : isTags
      ? tagRemaining
      : isFinalize
        ? Math.max(5, FINALIZE_EST_SEC - phaseElapsedSec)
        : null;

  return {
    steps,
    activeStepId,
    activeDetail,
    activeRemainingSec,
  };
}

/** Sum of active countdown + pending step estimates. */
export function estimateTotalRemainingSec(
  pipeline: ScanPipeline,
  liveActiveRemainingSec: number | null,
): number | null {
  if (!pipeline.activeStepId) return null;

  const activeRemaining = liveActiveRemainingSec ?? pipeline.activeRemainingSec;
  const activeStep = pipeline.steps.find((s) => s.status === 'active');
  const pendingSteps = pipeline.steps.filter((s) => s.status === 'pending');

  if (activeStep?.id === 'walk' && activeRemaining == null) return null;
  if (activeStep?.id === 'tags' && activeRemaining == null) return null;

  let total = activeRemaining ?? 0;
  for (const step of pendingSteps) {
    if (step.pendingEstimateSec != null) total += step.pendingEstimateSec;
  }

  if (activeRemaining == null && pendingSteps.every((s) => s.pendingEstimateSec == null)) {
    return null;
  }

  return total > 0 ? total : null;
}
